import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TenantPreferences.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function TenantPreferences() {
    const [preferences, setPreferences] = useState(null);
    const [publicTitle, setPublicTitle] = useState('');
    const [aboutText, setAboutText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPreferences();
    }, []);

    const fetchPreferences = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/tenant/preferences`, {
                withCredentials: true
            });
            setPreferences(response.data);
            setPublicTitle(response.data.public_title || '');
            setAboutText(response.data.about_text || '');
            setError('');
        } catch (error) {
            console.error('Error fetching preferences:', error);
            setError('Failed to load preferences');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        setError('');

        try {
            await axios.put(`${API_BASE}/tenant/preferences`, {
                publicTitle,
                aboutText
            }, {
                withCredentials: true
            });

            setMessage('Preferences saved successfully!');
            setTimeout(() => setMessage(''), 3000);

            // Refresh preferences
            fetchPreferences();
        } catch (error) {
            console.error('Error saving preferences:', error);
            setError(error.response?.data?.error || 'Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="tenant-preferences">
                <p>Loading preferences...</p>
            </div>
        );
    }

    return (
        <div className="tenant-preferences">
            <h2>Tenant Preferences</h2>
            <p className="preferences-description">
                Manage settings for your tenant's public gallery.
            </p>

            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}

            <form onSubmit={handleSave} className="preferences-form">
                <div className="form-section">
                    <h3>Public Gallery Settings</h3>

                    <div className="form-group">
                        <label htmlFor="publicTitle">
                            Public Gallery Title
                            <span className="label-hint">This title will be displayed on your public gallery page</span>
                        </label>
                        <input
                            type="text"
                            id="publicTitle"
                            value={publicTitle}
                            onChange={(e) => setPublicTitle(e.target.value)}
                            placeholder="e.g., My Photography Gallery"
                            required
                            disabled={saving}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="aboutText">
                            About Page Text
                            <span className="label-hint">If provided, an "About" link will appear on your public gallery. Leave empty to show only "Albums".</span>
                        </label>
                        <textarea
                            id="aboutText"
                            value={aboutText}
                            onChange={(e) => setAboutText(e.target.value)}
                            placeholder="Tell visitors about yourself or your gallery..."
                            disabled={saving}
                            rows={6}
                        />
                    </div>

                    <div className="preview-section">
                        <h4>Preview</h4>
                        <div className="gallery-preview">
                            <div className="preview-header">
                                <h1>{publicTitle || 'Gallery Title'}</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="submit"
                        className="save-button"
                        disabled={saving || !publicTitle.trim()}
                    >
                        {saving ? 'Saving...' : 'Save Preferences'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default TenantPreferences;
